import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  useToothSpacingEntries,
  useToothSpacingEntryDetail,
} from '../hooks/useToothSpacing';
import { getToothSpacingVideoUrl } from '../../../api/tooth-spacing';
import type { ToothSpacingEntryListItem } from '../../../api/tooth-spacing';
import { useAuth } from '../../auth/AuthProvider';
import { AuthenticatedVideo } from '../../../shared/components/AuthenticatedVideo';
import styles from './ToothSpacingReviewPanel.module.scss';

type ToothSpacingReviewPanelProps = {
  readonly patientId: string;
};

const STATUS_TABS = [
  { value: undefined, labelKey: 'clientDetail.toothSpacingUi.status.all' },
  { value: 'pending', labelKey: 'clientDetail.toothSpacingUi.status.pending' },
  {
    value: 'confirmed',
    labelKey: 'clientDetail.toothSpacingUi.status.confirmed',
  },
] as const;

function formatDate(value: string, locale: string): string {
  return new Date(`${value}T00:00:00`).toLocaleDateString(locale, {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });
}

export function ToothSpacingReviewPanel({
  patientId,
}: ToothSpacingReviewPanelProps) {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState<string | undefined>(undefined);
  const [expandedEntryId, setExpandedEntryId] = useState<string | null>(null);

  const entriesQuery = useToothSpacingEntries(patientId, { status: activeTab });
  const entries = entriesQuery.data ?? [];

  function handleToggleExpand(entry: ToothSpacingEntryListItem) {
    setExpandedEntryId((current) => (current === entry.id ? null : entry.id));
  }

  return (
    <div className={styles.panel}>
      <div className={styles.header}>
        <div>
          <h4 className={styles.title}>
            {t('clientDetail.toothSpacingUi.review.title')}
          </h4>
          <p className={styles.subtitle}>
            {t('clientDetail.toothSpacingUi.review.subtitle')}
          </p>
        </div>
        {!entriesQuery.isLoading && (
          <span className={styles.countBadge}>{entries.length}</span>
        )}
      </div>

      <div
        className={styles.tabs}
        role="group"
        aria-label={t('clientDetail.toothSpacingUi.filterByStatus')}
      >
        {STATUS_TABS.map((tab) => (
          <button
            key={tab.labelKey}
            type="button"
            className={`${styles.tab} ${activeTab === tab.value ? styles.active : ''}`}
            onClick={() => setActiveTab(tab.value)}
            aria-pressed={activeTab === tab.value}
          >
            {t(tab.labelKey)}
          </button>
        ))}
      </div>

      {entriesQuery.isLoading ? (
        <div
          className={styles.loadingList}
          aria-label={t('clientDetail.toothSpacingUi.loadingEntries')}
        >
          <span />
          <span />
          <span />
        </div>
      ) : entriesQuery.isError ? (
        <p className={styles.errorState} role="alert">
          {t('clientDetail.toothSpacingUi.loadEntriesFailed')}
        </p>
      ) : entries.length === 0 ? (
        <p className={styles.empty}>
          {t('clientDetail.toothSpacingUi.emptyEntries')}
        </p>
      ) : (
        <div className={styles.entryList}>
          {entries.map((entry) => (
            <EntryCard
              key={entry.id}
              entry={entry}
              patientId={patientId}
              isExpanded={expandedEntryId === entry.id}
              onToggleExpand={() => handleToggleExpand(entry)}
            />
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Entry card ───────────────────────────────────────────────────────────────

type EntryCardProps = {
  readonly entry: ToothSpacingEntryListItem;
  readonly patientId: string;
  readonly isExpanded: boolean;
  readonly onToggleExpand: () => void;
};

function EntryCard({
  entry,
  patientId,
  isExpanded,
  onToggleExpand,
}: EntryCardProps) {
  const { t, i18n } = useTranslation();

  return (
    <article
      className={`${styles.entryCard} ${isExpanded ? styles.expanded : ''}`}
    >
      <button
        type="button"
        className={styles.entryHeader}
        onClick={onToggleExpand}
        aria-expanded={isExpanded}
        aria-controls={`spacing-entry-${entry.id}`}
      >
        <div className={styles.headerMeta}>
          <span className={styles.dueDate}>
            {formatDate(entry.due_date, i18n.language)}
          </span>
          {entry.cycle_number != null && (
            <span className={styles.meta}>
              {t('clientDetail.toothSpacingUi.stageN', {
                number: entry.cycle_number,
              })}
            </span>
          )}
          {entry.stage_title && (
            <span className={styles.meta}>{entry.stage_title}</span>
          )}
          {entry.description_preview && (
            <span className={styles.previewText}>
              {entry.description_preview}
            </span>
          )}
        </div>
        <span className={styles.statusGroup}>
          <span className={`${styles.statusBadge} ${styles[entry.status]}`}>
            {entry.status === 'pending'
              ? t('clientDetail.toothSpacingUi.status.pending')
              : entry.status === 'confirmed'
                ? t('clientDetail.toothSpacingUi.status.confirmed')
                : entry.status}
          </span>
          <span className={styles.chevron} aria-hidden="true">
            ⌄
          </span>
        </span>
      </button>

      {isExpanded && (
        <div id={`spacing-entry-${entry.id}`}>
          <EntryDetail patientId={patientId} entryId={entry.id} />
        </div>
      )}
    </article>
  );
}

// ─── Entry detail ─────────────────────────────────────────────────────────────

type EntryDetailProps = {
  readonly patientId: string;
  readonly entryId: string;
};

function EntryDetail({ patientId, entryId }: EntryDetailProps) {
  const { t, i18n } = useTranslation();
  const { tokens } = useAuth();
  const token = tokens?.access_token ?? '';
  const detailQuery = useToothSpacingEntryDetail(patientId, entryId);

  if (detailQuery.isLoading) {
    return (
      <div className={styles.expandedSection}>
        <div className={styles.placeholder}>
          {t('clientDetail.toothSpacingUi.loadingDetails')}
        </div>
      </div>
    );
  }

  if (detailQuery.isError) {
    return (
      <div className={styles.errorState} role="alert">
        {t('clientDetail.toothSpacingUi.loadDetailsFailed')}
      </div>
    );
  }

  if (!detailQuery.data) return null;

  const detail = detailQuery.data;

  return (
    <div className={styles.expandedSection}>
      {detail.video_submitted ? (
        <div className={styles.videoSection}>
          <span className={styles.fieldLabel}>
            {t('clientDetail.toothSpacingUi.review.video')}
          </span>
          <AuthenticatedVideo
            path={getToothSpacingVideoUrl(patientId, entryId)}
            token={token}
          />
        </div>
      ) : detail.requires_video ? (
        <div className={styles.placeholder}>
          {t('clientDetail.toothSpacingUi.review.videoNotSubmitted')}
        </div>
      ) : null}

      <div className={styles.descriptionSection}>
        <span className={styles.fieldLabel}>
          {t('clientDetail.toothSpacingUi.review.description')}
        </span>
        {detail.description ? (
          <div className={styles.descriptionText}>{detail.description}</div>
        ) : (
          <div className={styles.placeholder}>
            {t('clientDetail.toothSpacingUi.review.noDescription')}
          </div>
        )}
      </div>

      {detail.confirmed_at && (
        <span className={styles.confirmedAt}>
          {t('clientDetail.toothSpacingUi.confirmedAt', {
            datetime: new Date(detail.confirmed_at).toLocaleString(
              i18n.language,
              {
                day: 'numeric',
                month: 'short',
                year: 'numeric',
                hour: '2-digit',
                minute: '2-digit',
              },
            ),
          })}
        </span>
      )}
    </div>
  );
}
